import React, { useState } from "react";
import {
  Shield, BookOpen, ChevronDown, ChevronRight, Heart, Activity, Target,
  Trash2, Eye, Lock, AlertTriangle,
} from "lucide-react";

/**
 * BdiPrivacyUtilityBanner — iter707d.
 *
 * GDPR-friendly disclosure + concrete "clinical utility for patient" block
 * for the BDI module. Sits at the top of BdiGenoLab as an expandable card.
 *
 * Aim: address the 3 questions a patient legitimately asks before engaging
 * with this kind of sensitive content:
 *   1. Is this a diagnosis?            → No (educational pathway model)
 *   2. Where do my data sit?            → Local + GDPR rights enumerated
 *   3. Why should I bother with this?   → Concrete benefits listed
 */
const TX = {
  bg: {
    title:        "Поверителност и клинична полза",
    sub:          "Кратък преглед преди да разгледате 12-те асоциационни карти.",
    expand:       "Виж подробно",
    collapse:     "Скрий",

    utilityTitle: "Какво получавате конкретно като пациент",
    utilities: [
      ["Ранна който може да променима рамка", "Виждате кои lifestyle пътища (възпаление, инсулин, ECM, естроген) са в зоната, която най-силно се асоциира със заболяване на гръдната тъкан — преди симптом, не след."],
      ["Конкретни лостове, не страх", "Всяка карта показва които може да променим фактори и цели за персонализирана профилактика. Не „имате 31%”, а „ето кои 3 фактора имат най-голям принос за тези 31% и какво можете да направите конкретно.”"],
      ["Превод lifestyle → биология", "Виждате точно кои сигнални пътища (NF-κB, IL-6, TNF-α) се модулират от Вашия начин на живот, хранене, сън, движение. Това превръща абстрактното „здравословен живот” в конкретен механизъм."],
      ["Симулатор на действие", "Pathway Sensitivity Slider Ви показва как ще се промени активационният скор, ако намалите hsCRP или подобрите инсулиновата чувствителност. Превръща абстрактно число в мотивация („ще падна от Moderate в Low band ако...”)"],
      ["Дневник за обсъждане с лекар", "Снапшотите на 3 месеца проследяват подобрението. Носите ясни данни на гинеколог / онколог за информиран разговор — не вместо лекар, а за по-добра консултация с лекар."],
    ],

    notUtilityTitle: "Какво НЕ е този модул",
    notUtility: [
      "Не е диагноза. Не казва „имате X заболяване”.",
      "Не е прогноза. Не предсказва развитие във времето.",
      "Не дава вероятност за рак. Не замества  / BOADICEA / BRCAPRO.",
      "Не предписва дози, медикаменти или режим. Винаги обсъждайте с лекар.",
    ],

    privacyTitle: "Как се пазят данните Ви (GDPR)",
    privacy: [
      ["Локални симулации", "Pathway Sensitivity slider-ите работят САМО в браузъра Ви. Стойностите им НЕ се изпращат към сървъра и не се запазват.", Lock],
      ["Минимум персонални данни", "Изчисленията използват само агрегатни BHI sub-scores. Никакви данни от трети страни не се продават или споделят.", Eye],
      ["Право на изтриване", "Можете да изтриете snapshots или цял профил по всяко време от Профил → Управление на данни.", Trash2],
      ["Право на коригиране", "Грешен лабораторен резултат? Редактирайте го директно — целият BDI се преизчислява автоматично.", Activity],
      ["Право на explainability", "Всяка карта показва weights + burdens + formula. Никакъв „black box AI” в активационния скор.", BookOpen],
    ],

    bottomLine: "Ако очаквате диагноза или вероятностна прогноза — този модул не е за това. Ако искате да разберете как Вашият начин на живот моделира тъканната среда и какво конкретно можете да промените — модулът е изграден точно за това.",
  },
  en: {
    title:        "Privacy & Clinical Utility",
    sub:          "Quick overview before you explore the 12 association cards.",
    expand:       "Show details",
    collapse:     "Hide",

    utilityTitle: "What you concretely get as a patient",
    utilities: [
      ["Modifiable early framing", "See which lifestyle pathways (inflammation, insulin, ECM, estrogen) are in the range most associated with breast-tissue conditions — before symptoms, not after."],
      ["Levers, not fear", "Each card shows modifiable factors and personalised prevention targets. Not 'you have 31%' but 'here are the 3 factors driving that 31% and what to do about them.'"],
      ["Lifestyle → biology translation", "See which signalling pathways (NF-κB, IL-6, TNF-α) your nutrition / sleep / movement modulate. Turns abstract 'healthy living' into a concrete mechanism."],
      ["Action simulator", "The Pathway Sensitivity Slider shows how the activation score changes if you reduce hsCRP or improve insulin sensitivity. Turns numbers into motivation ('I'll drop from Moderate to Low band if…')."],
      ["A doctor-discussion log", "3-month snapshots track improvement. Bring clear data to your gynaecologist / oncologist for a better-informed conversation — not in place of medical care."],
    ],

    notUtilityTitle: "What this module is NOT",
    notUtility: [
      "Not a diagnosis. It does NOT say 'you have X disease.'",
      "Not a prognosis. It does NOT predict future development.",
      "Not a cancer risk score. Does NOT replace  / BOADICEA / BRCAPRO.",
      "Does NOT prescribe doses, medications or regimens. Always discuss with a clinician.",
    ],

    privacyTitle: "How your data is protected (GDPR)",
    privacy: [
      ["Local simulations", "Pathway Sensitivity sliders run ONLY in your browser. Their values are NEVER sent to the server or persisted.", Lock],
      ["Minimal personal data", "Calculations use only aggregated BHI sub-scores. No third-party data is sold or shared.", Eye],
      ["Right to erasure", "Delete snapshots or your whole profile any time from Profile → Manage Data.", Trash2],
      ["Right to rectification", "Wrong lab result? Edit it directly — the whole BDI auto-recomputes.", Activity],
      ["Right to explainability", "Every card surfaces weights + burdens + formula. No 'black-box AI' in the activation score.", BookOpen],
    ],

    bottomLine: "If you expect a diagnosis or probability forecast — this isn't the module for that. If you want to understand how your lifestyle shapes the tissue environment and what concretely to change — this module is built exactly for that.",
  },
};

export default function BdiPrivacyUtilityBanner({ lang = "bg", isDark = false }) {
  const t = TX[lang] || TX.bg;
  const [open, setOpen] = useState(false);

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]"
    : "bg-white border-gray-200";

  return (
    <section
      className={`${cardCls} rounded-2xl border overflow-hidden`}
      data-testid="bdi-privacy-utility-banner"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full p-4 text-left flex items-start gap-3 hover:bg-black/5 dark:hover:bg-white/5 transition"
        data-testid="bdi-privacy-utility-toggle"
      >
        <div className={`p-2 rounded-lg shrink-0 ${isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-700"}`}>
          <Shield className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm sm:text-base leading-tight">{t.title}</h3>
          <p className="text-xs opacity-60 mt-0.5">{t.sub}</p>
        </div>
        {open ? <ChevronDown className="w-5 h-5 opacity-50 shrink-0" /> : <ChevronRight className="w-5 h-5 opacity-50 shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-5">
          {/* Clinical utility */}
          <div>
            <SectionTitle icon={<Heart className="w-3.5 h-3.5" />} text={t.utilityTitle} isDark={isDark} />
            <ul className="mt-2 space-y-2">
              {t.utilities.map(([title, body], i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
                    {i + 1}
                  </span>
                  <div>
                    <div className="font-semibold">{title}</div>
                    <p className="opacity-70 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* What it is NOT */}
          <div>
            <SectionTitle icon={<AlertTriangle className="w-3.5 h-3.5" />} text={t.notUtilityTitle} isDark={isDark} accent="amber" />
            <ul className="mt-2 space-y-1">
              {t.notUtility.map((x, i) => (
                <li key={i} className="text-xs flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">✕</span>
                  <span className="opacity-80">{x}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* GDPR */}
          <div>
            <SectionTitle icon={<Shield className="w-3.5 h-3.5" />} text={t.privacyTitle} isDark={isDark} accent="violet" />
            <ul className="mt-2 space-y-2">
              {t.privacy.map(([title, body, Icon], i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                  <div>
                    <div className="font-semibold">{title}</div>
                    <p className="opacity-70 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Bottom line */}
          <div
            className={`p-3 rounded-xl text-xs leading-relaxed italic ${
              isDark ? "bg-violet-500/10 text-violet-200" : "bg-violet-50 text-violet-900"
            }`}
            data-testid="bdi-bottom-line"
          >
            <Target className="w-4 h-4 inline-block mr-1 -mt-0.5" />
            {t.bottomLine}
          </div>
        </div>
      )}
    </section>
  );
}

function SectionTitle({ icon, text, isDark, accent = "emerald" }) {
  const cls = {
    emerald: isDark ? "text-emerald-300" : "text-emerald-700",
    amber:   isDark ? "text-amber-300"   : "text-amber-700",
    violet:  isDark ? "text-violet-300"  : "text-violet-700",
  }[accent];
  return (
    <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${cls}`}>
      {icon}
      {text}
    </div>
  );
}
